/**
 * 유심이동 시세표 — 개통방식(이동 / 신규)
 *
 * 화면은 통신사마다 "이동" 행과 "신규" 행을 한 묶음으로 보여준다 (모두의성지·제휴 유심이동 시세표 공통).
 * 서버는 usims 항목의 contractType 으로 구분한다 — NUMBER_CHANGE(번호이동) / NEW_ACTIVATION(신규).
 * 신규 행은 값을 하나도 안 넣으면 "미제공"으로 보고 제출·검증에서 뺀다 (기존 시세표는 신규 행이 비어 있으므로).
 * 서버 필드명·값이 바뀌면 USIM_OPEN_TYPE_API 만 고친다.
 */

import type { usimQuotationObject } from './request/quotation.request'

export type UsimOpenType = 'MOVE' | 'NEW'

/** 한 통신사 묶음 안의 행 순서 */
export const USIM_OPEN_TYPES: UsimOpenType[] = ['MOVE', 'NEW']

export const USIM_OPEN_TYPE_LABEL: Record<UsimOpenType, string> = {
  MOVE: '이동',
  NEW: '신규',
}

/** 서버 필드명·값 매핑 — 바뀌면 여기만 수정 */
export const USIM_OPEN_TYPE_API = {
  /** 백엔드가 개통방식을 받는지 — false 면 신규 행은 제출·검증에서 빠진다 */
  enabled: true,
  /** usims 항목 안에서 개통방식이 담기는 키 */
  field: 'contractType',
  value: {
    MOVE: 'NUMBER_CHANGE',
    NEW: 'NEW_ACTIVATION',
  } satisfies Record<UsimOpenType, string>,
} as const

/** 항목의 개통방식 — 화면 내부 openType 우선, 없으면 서버 필드. 둘 다 없거나 모르는 값이면 기존 데이터(이동)로 본다 */
export const readUsimOpenType = (item: unknown): UsimOpenType => {
  const record = item as Record<string, unknown> | null
  if (record?.openType === 'NEW' || record?.openType === 'MOVE') return record.openType
  const raw = record?.[USIM_OPEN_TYPE_API.field]
  return raw === USIM_OPEN_TYPE_API.value.NEW ? 'NEW' : 'MOVE'
}

/** 행에 입력된 값이 하나라도 있는지 — 신규 행 "미제공" 판단용 */
const hasUsimInput = (item: unknown) => {
  const record = item as Record<string, unknown> | null
  return [record?.fee, record?.price, record?.usePeriod, record?.periodType].some(
    (value) => value !== null && value !== undefined && value !== '',
  )
}

/** 빈 유심 행 — "항목 추가"와 신규 행 채우기에 쓴다. 필수 유지기간은 미선택(운영자가 직접 고른다) */
export const createEmptyUsimRow = (
  telecom: string,
  openType: UsimOpenType,
): usimQuotationObject =>
  ({
    type: 'ADD',
    telecom,
    fee: null,
    price: null,
    sortOrder: null,
    usePeriod: null,
    isMonth: true,
    openType,
  }) as unknown as usimQuotationObject

/**
 * 조회 응답 → 화면용 행 목록.
 * 통신사 순서(처음 나온 순)를 유지한 채 통신사마다 [이동, 신규] 한 묶음으로 정렬하고, 없는 행은 빈 행으로 채운다.
 */
export const normalizeUsimRows = (
  usims: usimQuotationObject[] | null | undefined,
): usimQuotationObject[] => {
  const list = usims ?? []
  const telecoms = Array.from(new Set(list.map((item) => item.telecom)))

  return telecoms.flatMap((telecom) =>
    USIM_OPEN_TYPES.map((openType) => {
      const found = list.find(
        (item) => item.telecom === telecom && readUsimOpenType(item) === openType,
      )
      return found ? { ...found, openType } : createEmptyUsimRow(telecom, openType)
    }),
  )
}

/** 통신사 묶음 — 화면 렌더·드래그용. index 는 form.usims 안의 실제 위치 */
export const groupUsimRows = (usims: usimQuotationObject[] | null | undefined) => {
  const list = usims ?? []
  const telecoms = Array.from(new Set(list.map((item) => item.telecom)))

  return telecoms.map((telecom) => ({
    telecom,
    rows: USIM_OPEN_TYPES.map((openType) => {
      const index = list.findIndex(
        (item) => item.telecom === telecom && readUsimOpenType(item) === openType,
      )
      return { openType, index, item: index >= 0 ? list[index] : null }
    }),
  }))
}

/** 통신사 묶음 순서대로 다시 펼친다 (드래그로 통신사 순서를 바꿀 때) */
export const reorderUsimRowsByTelecom = (
  usims: usimQuotationObject[] | null | undefined,
  telecomOrder: string[],
): usimQuotationObject[] => {
  const list = usims ?? []
  return telecomOrder.flatMap((telecom) =>
    USIM_OPEN_TYPES.flatMap((openType) =>
      list.filter(
        (item) => item.telecom === telecom && readUsimOpenType(item) === openType,
      ),
    ),
  )
}

/**
 * 제출·검증 대상 행인지
 * - 이동 행: 항상
 * - 신규 행: enabled 이고 값을 하나라도 넣었을 때만 (완전히 빈 신규 행은 미제공으로 보고 뺀다)
 */
export const isUsimRowSubmittable = (item: unknown) =>
  readUsimOpenType(item) === 'MOVE' || (USIM_OPEN_TYPE_API.enabled && hasUsimInput(item))

/** 검증 실패 시 포커스할 입력 — 같은 통신사 묶음 안에서 이동/신규 행을 구분한다 */
export const getUsimErrorField = (
  field: 'fee' | 'periodType' | 'usePeriod' | 'price',
  openType: UsimOpenType,
) => {
  const base = `usim${field[0].toUpperCase()}${field.slice(1)}`
  return openType === 'NEW' ? `${base}New` : base
}

/**
 * 제출용 폼 변환 — usims 를 서버 스펙으로 바꿔 끼운다. postQuotation / postPartnerQuotation 호출 직전에 사용.
 * - 내부 openType 필드는 지우고, enabled 면 개통방식을 서버 필드(contractType)로 실어 보낸다
 * - 비어 있는 신규 행은 isUsimRowSubmittable 에서 걸러진다
 */
export const withUsimOpenTypePayload = <T extends { usims?: usimQuotationObject[] }>(form: T): T => {
  if (!form.usims) return form
  const usims = form.usims.filter(isUsimRowSubmittable).map((item) => {
    const { openType: _openType, ...rest } = item as usimQuotationObject & { openType?: UsimOpenType }
    void _openType
    if (!USIM_OPEN_TYPE_API.enabled) return rest
    return {
      ...rest,
      [USIM_OPEN_TYPE_API.field]: USIM_OPEN_TYPE_API.value[readUsimOpenType(item)],
    }
  })
  return { ...form, usims: usims as usimQuotationObject[] }
}
